import React from 'react';
import { useTranslation } from 'next-i18next';
import { Button, Modal } from '@components/common';
import { useBjModal } from '@hooks/index';
import { ButtonProps } from '@type/anchors-buttons';

interface WithConfirmationProps {
  confirmationTitle?: string;
  confirmationDescription: string;
  confirmText?: string;
  onConfirm?: () => void | Promise<void>;
  isConfirmLoading?: boolean;
  cancelText?: string;
  onCancel?: () => void;
}

type DestructiveActionProps =
  | ({
    withConfirmation?: false;
  } & {
    [K in keyof WithConfirmationProps]?: undefined;
  } & ButtonProps)
  | ({
    withConfirmation: true;
  } & WithConfirmationProps & ButtonProps);

/**
 * @description A button component that performs a destructive action, such as deleting an item.
 * It can be used with or without a confirmation modal.
 *
 * @param {DestructiveActionProps} props - The properties for the component.
 * - **withConfirmation**: boolean - If true, the button will show a confirmation modal before executing the action.
 * - any prop that a Button accepts
 *
 * The below props can be provided only when `withConfirmation` is true:
 * - **confirmationTitle**: string - The title of the confirmation modal. (Optional)
 * - **confirmationDescription**: string - The description of the confirmation modal. (Required)
 * - **confirmText**: string - The text for the confirm button in the modal. (Optional)
 * - **onConfirm**: () => void | Promise<void> - A function to call when the confirm button is clicked, it can be sync or async. (Optional)
 * - **isConfirmLoading**: boolean - If true, shows a loading state on the confirm button. (Optional)
 * - **cancelText**: string - The text for the cancel button in the modal. (Optional)
 * - **onCancel**: () => void - A function to call when the cancel button is clicked. (Optional)
 */
export const DestructiveAction = (props: DestructiveActionProps) => {
  const { t } = useTranslation('common');

  const {
    withConfirmation = false,
    confirmationTitle,
    confirmationDescription,
    confirmText,
    onConfirm,
    isConfirmLoading,
    cancelText,
    onCancel,
    ...buttonProps
  } = props;

  const {
    isOpen: isConfirmationOpen,
    openModal: openConfirmationModal,
    closeModal: closeConfirmationModal,
  } = useBjModal();

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    buttonProps.onClick?.(e);
    if (withConfirmation) {
      openConfirmationModal();
    }
  };

  const handleConfirm = async () => {
    // This will work for both synchronous and asynchronous onConfirm functions
    await Promise.resolve(onConfirm?.());
    closeConfirmationModal();
  };

  const handleCancel = () => {
    onCancel?.();
    closeConfirmationModal();
  };

  if (!withConfirmation) {
    return <Button {...buttonProps} />;
  }

  return (
    <>
      <Button {...buttonProps} onClick={handleOnClick} />
      {withConfirmation && (
        <Modal isOpen={isConfirmationOpen} onClose={handleCancel} size="sm">
          <Modal.Header onClose={closeConfirmationModal}>
            <h5 className="text-xl">{confirmationTitle || t('global.action.confirm')}</h5>
          </Modal.Header>
          <Modal.Body>
            <p>{confirmationDescription}</p>
          </Modal.Body>
          <Modal.Footer className="flex justify-end gap-2">
            <Button onClick={handleCancel} styling="outline" color="light">
              {cancelText || t('global.action.no')}
            </Button>
            <Button onClick={() => { void handleConfirm(); }} color="error" isLoading={isConfirmLoading}>
              {confirmText || t('global.action.yes')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

